import { z } from 'zod';
import { audienceSpecSchema } from '../audience/index';

/**
 * Contracten van de module documents: werkinstructies en veiligheidsvoorschriften met versies
 * (Stap 20).
 *
 * Het verschil met een bijlage bij een bericht is **de versie**. Een werkinstructie van vorig jaar
 * is niet fout maar vervallen, en een medewerker hoort uitsluitend de geldige versie te zien. Wie
 * v1 bevestigde en nu v2 krijgt, moet opnieuw bevestigen — en bij een ongeval is de vraag precies
 * welke versie iemand gezien heeft.
 *
 * Eerste slice (demoroute, ADR-0052): mappen, documenten met een doelgroep, versies met geldigheid
 * en kennisname per versie. Wat er nog niet is: een geldigheidsdatum met herinnering aan de
 * eigenaar, offline markeren, en een export per versie — zie `docs/product/demo-schuld.md`.
 */

/**
 * Drie statussen, en de middelste is de enige die een medewerker ziet.
 *
 * `draft` staat klaar maar is nog niet geldig; `published` is de geldige versie; `superseded` is
 * vervangen door een nieuwere. Een vervallen versie is niet fout maar verouderd, en daarom wordt ze
 * bewaard: het bewijs dat iemand v1 bevestigde, moet naar v1 kunnen verwijzen.
 */
export const documentVersionStatusSchema = z.enum(['draft', 'published', 'superseded']);
export type DocumentVersionStatus = z.infer<typeof documentVersionStatusSchema>;

export const documentFolderSchema = z.object({
  id: z.guid(),
  name: z.string(),
  /** Eén niveau diep in deze slice: een map in een map vraagt een boomweergave. */
  parentId: z.guid().nullable(),
  documentCount: z.number().int(),
});
export type DocumentFolder = z.infer<typeof documentFolderSchema>;

export const documentFolderListSchema = z.object({ items: z.array(documentFolderSchema) });
export type DocumentFolderList = z.infer<typeof documentFolderListSchema>;

export const documentVersionSchema = z.object({
  id: z.guid(),
  documentId: z.guid(),
  /** Wat de beheerder erop schreef: "v2", "2026-03", "rev. B". Geen oplopend nummer van ons. */
  versionLabel: z.string(),
  fileId: z.guid(),
  status: documentVersionStatusSchema,
  /** Of een medewerker moet bevestigen dat hij déze versie gelezen heeft. */
  requiresAcknowledgement: z.boolean(),
  /** Waarom deze versie er is. Verschijnt bij de medewerker: "wat is er veranderd?" */
  changeNote: z.string(),
  publishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /** Aantallen, nooit namen (ADR-0014). */
  acknowledgedCount: z.number().int(),
});
export type DocumentVersion = z.infer<typeof documentVersionSchema>;

export const documentSchema = z.object({
  id: z.guid(),
  folderId: z.guid().nullable(),
  folderName: z.string().nullable(),
  title: z.string(),
  audience: audienceSpecSchema,
  audienceDescription: z.string(),
  /** De geldige versie, of leeg als er nog geen gepubliceerd is. */
  currentVersion: documentVersionSchema.nullable(),
  versionCount: z.number().int(),
  createdAt: z.string().datetime(),
});
export type Document = z.infer<typeof documentSchema>;

export const documentListSchema = z.object({ items: z.array(documentSchema) });
export type DocumentList = z.infer<typeof documentListSchema>;

export const documentVersionListSchema = z.object({ items: z.array(documentVersionSchema) });
export type DocumentVersionList = z.infer<typeof documentVersionListSchema>;

/**
 * Eén document zoals de medewerker het ziet: alleen de geldige versie.
 *
 * Geen `versions`-lijst en geen oude bestand-ids. Een vervallen werkinstructie openen is precies
 * wat een documentbeheersysteem hoort te voorkomen, en de goedkoopste manier om dat te garanderen
 * is: de oude versies staan niet in het antwoord.
 */
export const myDocumentSchema = z.object({
  documentId: z.guid(),
  versionId: z.guid(),
  title: z.string(),
  folderName: z.string().nullable(),
  versionLabel: z.string(),
  changeNote: z.string(),
  fileId: z.guid(),
  requiresAcknowledgement: z.boolean(),
  /** Wanneer ík deze versie bevestigde; leeg als dat nog niet gebeurd is. */
  acknowledgedAt: z.string().datetime().nullable(),
  publishedAt: z.string().datetime().nullable(),
});
export type MyDocument = z.infer<typeof myDocumentSchema>;

export const myDocumentListSchema = z.object({ items: z.array(myDocumentSchema) });
export type MyDocumentList = z.infer<typeof myDocumentListSchema>;

export const createFolderRequestSchema = z.object({
  name: z.string().trim().min(1, 'Geef de map een naam.').max(80),
  parentId: z.guid().nullable().default(null),
});
export type CreateFolderRequest = z.infer<typeof createFolderRequestSchema>;

export const createDocumentRequestSchema = z.object({
  title: z.string().trim().min(1, 'Geef het document een titel.').max(200),
  folderId: z.guid().nullable().default(null),
  audience: audienceSpecSchema,
});
export type CreateDocumentRequest = z.infer<typeof createDocumentRequestSchema>;

export const createVersionRequestSchema = z.object({
  versionLabel: z.string().trim().min(1, 'Geef de versie een aanduiding, bijvoorbeeld v2.').max(40),
  fileId: z.guid(),
  changeNote: z.string().trim().max(1000).default(''),
  requiresAcknowledgement: z.boolean().default(false),
});
export type CreateVersionRequest = z.infer<typeof createVersionRequestSchema>;

/**
 * Het bewijs per versie: wie bevestigde dat hij déze versie gelezen heeft.
 *
 * Mét namen, en dat is geen uitzondering op ADR-0014 maar de kern van waarom dit bestaat: bij een
 * ongeval is "wie heeft de veiligheidsinstructie van die versie bevestigd" de eerste vraag. Het
 * zit achter `documents.evidence.view`, een recht dat in geen enkele medewerkersrol zit.
 */
export const documentEvidenceRowSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  employeeNumber: z.string().nullable(),
  acknowledgedAt: z.string().datetime(),
});
export type DocumentEvidenceRow = z.infer<typeof documentEvidenceRowSchema>;

export const documentEvidenceSchema = z.object({
  versionId: z.guid(),
  versionLabel: z.string(),
  /** Hoeveel mensen de versie kregen. De noemer bij het aantal bevestigingen. */
  recipientCount: z.number().int(),
  items: z.array(documentEvidenceRowSchema),
});
export type DocumentEvidence = z.infer<typeof documentEvidenceSchema>;

/** Hoeveel mensen een herinnering kregen (Extra 6, punt 4). */
export const documentReminderResultSchema = z.object({ sent: z.number().int() });
export type DocumentReminderResult = z.infer<typeof documentReminderResultSchema>;
